import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function StatCard({ label, value, sub, color, icon }) {
  return (
    <div className="card animate-fadeUp" style={{ padding: '20px 22px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, color: '#62615D', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#202326', fontFamily: "'DM Sans', sans-serif" }}>{value}</div>
          {sub && <div style={{ fontSize: 11, color: '#62615D', marginTop: 4 }}>{sub}</div>}
        </div>
        <div style={{ fontSize: 28, opacity: 0.7 }}>{icon}</div>
      </div>
    </div>
  )
}

function RevenueBar({ month, invoiced, collected, max }) {
  const barMax = Math.max(max, 1)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      <div style={{ width: 32, fontSize: 11, color: '#62615D', textAlign: 'right', flexShrink: 0 }}>{month}</div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{ height: 10, borderRadius: 5, background: '#10B981', width: `${(invoiced / barMax) * 100}%`, minWidth: invoiced > 0 ? 4 : 0, transition: 'width 0.5s ease' }} />
          {invoiced > 0 && <span style={{ fontSize: 10, color: '#10B981', fontWeight: 600, whiteSpace: 'nowrap' }}>${invoiced.toFixed(0)}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ height: 10, borderRadius: 5, background: '#CBB17B', width: `${(collected / barMax) * 100}%`, minWidth: collected > 0 ? 4 : 0, transition: 'width 0.5s ease' }} />
          {collected > 0 && <span style={{ fontSize: 10, color: '#CBB17B', fontWeight: 600, whiteSpace: 'nowrap' }}>${collected.toFixed(0)}</span>}
        </div>
      </div>
    </div>
  )
}

export default function RevenueDashboard() {
  const { profile } = useAuth()
  const [invoices, setInvoices] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear())

  useEffect(() => {
    async function load() {
      const { data: c } = await supabase.from('clients').select('id, full_name, business_name').eq('cpa_id', profile.id)
      const clientIds = (c || []).map(x => x.id)
      setClients(c || [])

      if (clientIds.length === 0) { setLoading(false); return }

      const { data: inv } = await supabase
        .from('invoices')
        .select('*')
        .in('client_id', clientIds)
        .neq('status', 'draft')
        .neq('status', 'cancelled')
        .order('created_at', { ascending: true })

      setInvoices(inv || [])
      setLoading(false)
    }
    load()
  }, [])

  const yearInvoices = invoices.filter(inv => {
    const invYear = new Date(inv.created_at).getFullYear()
    return invYear === yearFilter
  })

  // Key metrics
  const totalInvoiced = yearInvoices.reduce((s, i) => s + (i.total_amount || 0), 0)
  const totalCollected = yearInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0)
  const totalOutstanding = yearInvoices.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0)
  const totalOverdue = yearInvoices.filter(i => i.status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0)
  const collectionRate = totalInvoiced > 0 ? Math.round((totalCollected / totalInvoiced) * 100) : 0
  const avgInvoice = yearInvoices.length > 0 ? totalInvoiced / yearInvoices.length : 0

  // Monthly data
  const monthlyData = MONTHS.map((m, idx) => {
    const monthInvs = yearInvoices.filter(i => new Date(i.created_at).getMonth() === idx)
    return {
      month: m,
      invoiced: monthInvs.reduce((s, i) => s + (i.total_amount || 0), 0),
      collected: monthInvs.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0),
    }
  })
  const monthMax = Math.max(...monthlyData.map(m => Math.max(m.invoiced, m.collected)), 1)

  // Top clients by revenue
  const clientRevenue = clients.map(c => {
    const cInvs = yearInvoices.filter(i => i.client_id === c.id)
    return {
      ...c,
      invoiced: cInvs.reduce((s, i) => s + (i.total_amount || 0), 0),
      collected: cInvs.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0),
      count: cInvs.length
    }
  }).filter(c => c.invoiced > 0).sort((a, b) => b.invoiced - a.invoiced).slice(0, 8)

  // Invoice status breakdown
  const statusBreakdown = [
    { label: 'Paid', count: yearInvoices.filter(i => i.status === 'paid').length, amount: totalCollected, color: '#10B981' },
    { label: 'Outstanding', count: yearInvoices.filter(i => i.status === 'sent').length, amount: totalOutstanding - totalOverdue, color: '#F59E0B' },
    { label: 'Overdue', count: yearInvoices.filter(i => i.status === 'overdue').length, amount: totalOverdue, color: '#EF4444' },
  ]

  // Available years
  const years = [...new Set(invoices.map(i => new Date(i.created_at).getFullYear()))].sort((a, b) => b - a)
  if (!years.includes(new Date().getFullYear())) years.unshift(new Date().getFullYear())

  if (loading) return (
    <div style={{ display: 'grid', gap: 16 }}>
      {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
    </div>
  )

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 24, background: '#202326', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 4 }}>Revenue Dashboard</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>Track invoiced, collected, and outstanding revenue.</p>
          </div>
          <select value={yearFilter} onChange={e => setYearFilter(parseInt(e.target.value))}
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#CBB17B', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {years.map(y => <option key={y} value={y} style={{ background: '#202326' }}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Key metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        <StatCard label="Total Invoiced" value={`$${totalInvoiced.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={`${yearInvoices.length} invoices`} color="#CBB17B" icon="🧾" />
        <StatCard label="Total Collected" value={`$${totalCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={`${collectionRate}% collection rate`} color="#10B981" icon="💰" />
        <StatCard label="Outstanding" value={`$${totalOutstanding.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={totalOverdue > 0 ? `$${totalOverdue.toFixed(2)} overdue` : 'No overdue'} color={totalOverdue > 0 ? '#EF4444' : '#F59E0B'} icon="⏳" />
        <StatCard label="Avg Invoice" value={`$${avgInvoice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={`${clients.length} active clients`} color="#8B5CF6" icon="📊" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Monthly Revenue Chart */}
        <div className="card" style={{ padding: '20px 22px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>Monthly Revenue — {yearFilter}</h3>
            <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#10B981', display: 'inline-block' }} />Invoiced</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#CBB17B', display: 'inline-block' }} />Collected</span>
            </div>
          </div>
          <div>
            {monthlyData.map(m => (
              <RevenueBar key={m.month} month={m.month} invoiced={m.invoiced} collected={m.collected} max={monthMax} />
            ))}
          </div>
        </div>

        {/* Invoice Status Breakdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Collection rate */}
          <div className="card" style={{ padding: '20px 22px' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Collection Rate</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
                <svg width="80" height="80" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="30" fill="none" stroke="#F0EBE0" strokeWidth="10" />
                  <circle cx="40" cy="40" r="30" fill="none" stroke={collectionRate >= 80 ? '#10B981' : collectionRate >= 50 ? '#F59E0B' : '#EF4444'}
                    strokeWidth="10" strokeLinecap="round" strokeDasharray={`${collectionRate * 1.885} 188.5`}
                    strokeDashoffset="47.1" transform="rotate(-90 40 40)" style={{ transition: 'stroke-dasharray 0.8s ease' }} />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#202326' }}>{collectionRate}%</div>
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#202326', fontWeight: 600, marginBottom: 4 }}>
                  {collectionRate >= 80 ? '🟢 Excellent' : collectionRate >= 60 ? '🟡 Good' : '🔴 Needs Attention'}
                </div>
                <div style={{ fontSize: 12, color: '#62615D' }}>${totalCollected.toFixed(2)} collected of ${totalInvoiced.toFixed(2)} invoiced</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {statusBreakdown.map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 12, color: '#202326' }}>{s.label}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#202326' }}>${s.amount.toFixed(2)}</div>
                  <div style={{ fontSize: 11, color: '#62615D' }}>({s.count})</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick stats */}
          <div className="card" style={{ padding: '16px 20px' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Quick Stats</h3>
            {[
              { label: 'Invoices This Year', value: yearInvoices.length },
              { label: 'Paid Invoices', value: yearInvoices.filter(i => i.status === 'paid').length },
              { label: 'Overdue Invoices', value: yearInvoices.filter(i => i.status === 'overdue').length, alert: true },
              { label: 'Active Clients', value: clients.length },
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F7F3EB' }}>
                <span style={{ fontSize: 12, color: '#62615D' }}>{s.label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: s.alert && s.value > 0 ? '#EF4444' : '#202326' }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top clients */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: '#F7F3EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>Top Clients by Revenue — {yearFilter}</h3>
          <span style={{ fontSize: 12, color: '#62615D' }}>Sorted by total invoiced</span>
        </div>
        {clientRevenue.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#62615D', fontSize: 13 }}>No revenue data for {yearFilter}</div>
        ) : (
          clientRevenue.map((c, i) => {
            const pct = totalInvoiced > 0 ? (c.invoiced / totalInvoiced) * 100 : 0
            const collPct = c.invoiced > 0 ? Math.round((c.collected / c.invoiced) * 100) : 0
            return (
              <div key={c.id} style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: i < clientRevenue.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#202326', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#CBB17B', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#202326', marginBottom: 4 }}>{c.business_name || c.full_name}</div>
                  <div style={{ height: 6, background: '#F0EBE0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#CBB17B', width: `${pct}%`, borderRadius: 3, transition: 'width 0.5s ease' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#202326' }}>${c.invoiced.toFixed(2)}</div>
                  <div style={{ fontSize: 11, color: collPct >= 80 ? '#10B981' : '#F59E0B' }}>{collPct}% collected</div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
